<script setup>
import { FormCheckboxGroup, FormCheckboxGroupOption, FormInput, FormListbox, FormModelListbox, FormTags, FormTextarea } from "@Components/Form";
import { LoadingIcon, MagicIcon, YouTubeIcon } from "@Icons/index";
import {
    BoldIcon,
    ChevronUpIcon,
    CodeIcon,
    ExternalLinkIcon,
    ImageIcon,
    ImagesIcon,
    LayoutTemplateIcon,
    LinkIcon,
    ListChecksIcon,
    ListIcon,
    Settings2Icon,
    Share2Icon,
    SmileIcon,
    TableIcon,
    TableOfContentsIcon,
} from "lucide-vue-next";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import { useI18n } from "vue-i18n";
import { PrimaryButton } from "@Components/Button";
import { useForm } from "@/form";
import ProfilePart from "./ProfilePart.vue";
import { computed, ref } from "vue";
import { writingStyles } from "@Utils/persona";
import CustomLinksPart from "./CustomLinksPart.vue";
import { useStore } from "@/Stores/templates";
import UseTemplateModal from "@Components/Templates/UseTemplateModal.vue";
import _cloneDeep from "lodash/cloneDeep";
import _merge from "lodash/merge";
import { models as textModels, getProvider as getTextProvider, getGroup as getTextGroup } from "@Utils/text-models";
import { models as imagesModels, getProvider as getImageProvider, getGroup as getImageGroup } from "@Utils/image-models";
import _map from "lodash/map";
import _groupBy from "lodash/groupBy";
import Tooltip from "@Components/Tooltip.vue";

const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
    profiles: {
        type: Array,
        default: [],
    },
});

const { t: __ } = useI18n();

const useTemplateModal = ref();
const generateKeywordsForm = useForm({
    title: props.modelValue.title,
    language: props.modelValue.language,
});
const generateBriefForm = useForm({
    title: props.modelValue.title,
    language: props.modelValue.language,
});

const formattedWritingStyles = computed(() => {
    return writingStyles.map((s) => {
        return {
            key: s.key,
            name: __(s.name),
        };
    });
});

const generateKeywords = async () => {
    if (generateKeywordsForm.processing) return;
    if (props.modelValue.title.trim().length === 0) return;

    generateKeywordsForm.title = props.modelValue.title;
    generateKeywordsForm.language = props.modelValue.language;

    const response = await generateKeywordsForm.post("single_mode_generate_keywords");

    if (response.keywords) {
        props.modelValue.settings.keywords.custom = response.keywords;
    }
};

const generateBrief = async () => {
    if (generateBriefForm.processing) return;
    if (props.modelValue.title.trim().length === 0) return;

    generateBriefForm.title = props.modelValue.title;
    generateBriefForm.language = props.modelValue.language;

    const response = await generateBriefForm.post("single_mode_generate_brief");

    if (response.brief) {
        props.modelValue.settings.generation_brief = response.brief;
    }
};

const useTemplate = (template) => {
    // empty all array settings
    props.modelValue.settings.custom_links.links = [];
    props.modelValue.settings.keywords.custom = [];

    props.modelValue.settings = _merge(props.modelValue.settings, _cloneDeep(template.single_mode_formatted_payload.settings));
    props.modelValue.persona_id = template.single_mode_formatted_payload.persona_id;

    useTemplateModal.value.hide();
};

const textModelsGroups = _map(_groupBy(_cloneDeep(textModels), "group"), (items, group) => {
    return {
        name: getTextGroup(group).name,
        items: items,
    };
});

const imagesModelsGroups = _map(_groupBy(_cloneDeep(imagesModels), "group"), (items, group) => {
    return {
        name: getImageGroup(group).name,
        items: items,
    };
});
</script>
<template>
    <div class="space-y-6">
        <div class="flex justify-center">
            <PrimaryButton @click.prevent="$refs.useTemplateModal.show()" :icon="LayoutTemplateIcon">{{ __("Utiliser un template") }}</PrimaryButton>
            <UseTemplateModal ref="useTemplateModal" @use="useTemplate" :show-advanced="false" />
        </div>

        <Disclosure as="div" v-slot="{ open }" :defaultOpen="true">
            <DisclosureButton
                :class="[
                    open ? 'rounded-t-lg' : 'rounded-lg',
                    'w-full flex justify-between px-6 py-3 text-sm font-medium text-left text-gray-900 border border-gray-300 dark:text-white dark:border-gray-800 focus:outline-none focus-visible:ring-gray-500/75 focus-visible:ring hover:bg-gray-50 dark:hover:bg-gray-900',
                ]">
                <span>{{ __("Profil de rédacteur") }}</span>
                <ChevronUpIcon :class="open ? 'rotate-180 transform' : ''" class="w-5 h-5 text-gray-900 dark:text-white" />
            </DisclosureButton>
            <DisclosurePanel as="div" class="px-6 py-6 space-y-6 rounded-b-lg border-r border-b border-l border-gray-300 dark:border-gray-800">
                <ProfilePart :modelValue="modelValue" @update:modelValue="(e) => (modelValue = e)" :profiles="profiles" />
            </DisclosurePanel>
        </Disclosure>

        <Disclosure as="div" v-slot="{ open }" :defaultOpen="true">
            <DisclosureButton
                :class="[
                    open ? 'rounded-t-lg' : 'rounded-lg',
                    'w-full flex justify-between px-6 py-3 text-sm font-medium text-left text-gray-900 border border-gray-300 dark:text-white dark:border-gray-800 focus:outline-none focus-visible:ring-gray-500/75 focus-visible:ring hover:bg-gray-50 dark:hover:bg-gray-900',
                ]">
                <span>{{ __("Options du texte") }}</span>
                <ChevronUpIcon :class="open ? 'rotate-180 transform' : ''" class="w-5 h-5 text-gray-900 dark:text-white" />
            </DisclosureButton>
            <DisclosurePanel as="div" class="px-6 py-6 space-y-6 rounded-b-lg border-r border-b border-l border-gray-300 dark:border-gray-800">
                <FormModelListbox v-model="modelValue.settings.models.text" :groups="textModelsGroups" required :label="__('Modèle de texte IA')">
                    <template v-slot:details="{ item }">
                        <div class="flex flex-col gap-3 p-3">
                            <div>
                                <span class="text-xs text-gray-600 dark:text-gray-400">{{ getTextProvider(item.provider).name }}&nbsp;/&nbsp;</span>
                                <span class="text-xs text-gray-900 dark:text-white">{{ item.name }}</span>
                            </div>
                            <div v-if="item.description" class="w-[14rem] text-xs text-gray-600 dark:text-gray-400">{{ item.description }}</div>
                            <div v-if="item.quality">
                                <span class="text-xs text-gray-900 dark:text-white">{{ __("Qualité") }}</span>
                                <div class="w-[14rem] grid grid-cols-10 gap-1 mt-1">
                                    <div
                                        v-for="i in 10"
                                        :key="i"
                                        :class="i <= item.quality ? 'bg-gray-900 dark:bg-white' : 'bg-gray-300 dark:bg-gray-800'"
                                        class="h-1 rounded-full"></div>
                                </div>
                            </div>
                            <div v-if="item.speed">
                                <span class="text-xs text-gray-900 dark:text-white">{{ __("Vitesse") }}</span>
                                <div class="w-[14rem] grid grid-cols-10 gap-1 mt-1">
                                    <div
                                        v-for="i in 10"
                                        :key="i"
                                        :class="i <= item.speed ? 'bg-gray-900 dark:bg-white' : 'bg-gray-300 dark:bg-gray-800'"
                                        class="h-1 rounded-full"></div>
                                </div>
                            </div>
                            <div v-if="item.price">
                                <span class="text-xs text-gray-900 dark:text-white">${{ item.price }}</span>
                                <span class="text-xs text-gray-600 dark:text-gray-400">&nbsp;/1M tokens</span>
                            </div>
                        </div>
                    </template>
                </FormModelListbox>

                <div class="grid grid-cols-2 gap-3">
                    <FormListbox
                        v-model="modelValue.settings.text_length"
                        :label="__('Longueur du contenu')"
                        :items="[
                            { id: 'small', name: __('Court') },
                            { id: 'medium', name: __('Moyen') },
                            { id: 'large', name: __('Long') },
                        ]"
                        required />
                    <FormListbox :label="__('Style d\'écriture')" :items="formattedWritingStyles" itemKey="key" v-model="modelValue.settings.writing_style" />
                </div>
                <FormTextarea
                    v-model="modelValue.settings.generation_brief"
                    :label="__('Brief pour la rédaction IA')"
                    :rows="7"
                    :left-actions="[
                        {
                            id: 'generate',
                            icon: MagicIcon,
                            processing: generateBriefForm.processing,
                            disabled: modelValue.title.trim().length === 0,
                            label: __('Générer'),
                            onClick: generateBrief,
                        },
                        {
                            id: 'magic-prompt',
                        },
                    ]"
                    :right-actions="[
                        {
                            id: 'maximize',
                        },
                    ]">
                </FormTextarea>

                <FormTags v-model="modelValue.settings.keywords.custom" :label="__('Intégration de mots clés')" class="pr-10">
                    <template #action>
                        <div class="absolute inset-y-0 right-0 flex mt-3 mr-3">
                            <Tooltip :content="__('Générer des mots clés')" placement="top">
                                <button
                                    @click.stop="generateKeywords"
                                    type="button"
                                    :class="['flex items-center gap-1.5  cursor-pointer text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 hover:text-gray-900',
                                    ]">
                                    <LoadingIcon v-if="generateKeywordsForm.processing" class="size-4" />
                                    <MagicIcon v-else class="size-4" />
                                </button>
                            </Tooltip>
                        </div>
                    </template>
                </FormTags>

                <div>
                    <div class="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                        {{ __("Options de rédaction") }}
                    </div>

                    <FormCheckboxGroup>
                        <div class="grid grid-cols-2 gap-3 mt-2 2xl:grid-cols-3 lg:grid-cols-2 sm:grid-cols-3">
                            <FormCheckboxGroupOption
                                v-model="modelValue.settings.summary.enabled"
                                :title="__('Sommaire')"
                                :icon="ListChecksIcon"
                                :tooltip="__('Active l\'ajout d\'un sommaire')"
                                size="small" />
                            <FormCheckboxGroupOption
                                v-model="modelValue.settings.bold_words.enabled"
                                :title="__('Mots en gras')"
                                :icon="BoldIcon"
                                :tooltip="__('Active l\'ajout de mots en gras')"
                                size="small" />
                            <FormCheckboxGroupOption
                                v-model="modelValue.settings.emojis.enabled"
                                :title="__('Emojis')"
                                :icon="SmileIcon"
                                :tooltip="__('Active l\'ajout d\'emojis')"
                                size="small" />
                            <FormCheckboxGroupOption
                                v-model="modelValue.settings.lists.enabled"
                                :title="__('Listes')"
                                :icon="ListIcon"
                                :tooltip="__('Active l\'ajout de listes')"
                                size="small" />
                            <FormCheckboxGroupOption
                                v-model="modelValue.settings.tables.enabled"
                                :title="__('Tableaux')"
                                :icon="TableIcon"
                                :tooltip="__('Active l\'ajout de tableaux')"
                                size="small" />
                            <!-- <FormCheckboxGroupOption
                                v-model="modelValue.settings.toolbox.enabled"
                                :title="__('ToolBox')"
                                :icon="CodeIcon"
                                :tooltip="__('Active l\'ajout de ToolBox')"
                                size="small" /> -->
                            <FormCheckboxGroupOption
                                v-model="modelValue.settings.faq.enabled"
                                :title="__('FAQ')"
                                :icon="TableOfContentsIcon"
                                :tooltip="__('Active l\'ajout d\'une FAQ')"
                                size="small" />
                        </div>
                    </FormCheckboxGroup>
                </div>
            </DisclosurePanel>
        </Disclosure>

        <Disclosure as="div" v-slot="{ open }" :defaultOpen="true">
            <DisclosureButton
                :class="[
                    open ? 'rounded-t-lg' : 'rounded-lg',
                    'w-full flex justify-between px-6 py-3 text-sm font-medium text-left text-gray-900 border border-gray-300 dark:text-white dark:border-gray-800 focus:outline-none focus-visible:ring-gray-500/75 focus-visible:ring hover:bg-gray-50 dark:hover:bg-gray-900',
                ]">
                <span>{{ __("Médias") }}</span>
                <ChevronUpIcon :class="open ? 'rotate-180 transform' : ''" class="w-5 h-5 text-gray-900 dark:text-white" />
            </DisclosureButton>
            <DisclosurePanel as="div" class="px-6 py-6 space-y-6 rounded-b-lg border-r border-b border-l border-gray-300 dark:border-gray-800">
                <FormModelListbox v-model="modelValue.settings.models.image" :groups="imagesModelsGroups" required :label="__('Modèle d\'images')">
                    <template v-slot:details="{ item }">
                        <div class="flex flex-col gap-3 p-3">
                            <div>
                                <span class="text-xs text-gray-600 dark:text-gray-400">{{ getImageProvider(item.provider).name }}&nbsp;/&nbsp;</span>
                                <span class="text-xs text-gray-900 dark:text-white">{{ item.name }}</span>
                            </div>
                            <div v-if="item.description" class="w-[14rem] text-xs text-gray-600 dark:text-gray-400">{{ item.description }}</div>
                            <div v-if="item.quality">
                                <span class="text-xs text-gray-900 dark:text-white">{{ __("Qualité") }}</span>
                                <div class="w-[14rem] grid grid-cols-10 gap-1 mt-1">
                                    <div
                                        v-for="i in 10"
                                        :key="i"
                                        :class="i <= item.quality ? 'bg-gray-900 dark:bg-white' : 'bg-gray-300 dark:bg-gray-800'"
                                        class="h-1 rounded-full"></div>
                                </div>
                            </div>
                            <div v-if="item.speed">
                                <span class="text-xs text-gray-900 dark:text-white">{{ __("Vitesse") }}</span>
                                <div class="w-[14rem] grid grid-cols-10 gap-1 mt-1">
                                    <div
                                        v-for="i in 10"
                                        :key="i"
                                        :class="i <= item.speed ? 'bg-gray-900 dark:bg-white' : 'bg-gray-300 dark:bg-gray-800'"
                                        class="h-1 rounded-full"></div>
                                </div>
                            </div>
                            <div v-if="item.price">
                                <span class="text-xs text-gray-900 dark:text-white">${{ item.price }}</span>
                                <span class="text-xs text-gray-600 dark:text-gray-400">&nbsp;/image</span>
                            </div>
                        </div>
                    </template>
                </FormModelListbox>

                <div>
                    <div class="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                        {{ __("Intégration des médias") }}
                    </div>
                    <FormCheckboxGroup>
                        <div class="grid grid-cols-2 gap-3 mt-2 2xl:grid-cols-3 lg:grid-cols-2 sm:grid-cols-3">
                            <FormCheckboxGroupOption
                                v-model="modelValue.settings.images.thumbnail.enabled"
                                :title="__('Image mise en avant')"
                                :icon="ImageIcon"
                                :tooltip="__('Générer une image de mise en avant')"
                                size="small" />
                            <FormCheckboxGroupOption
                                v-model="modelValue.settings.images.content.enabled"
                                :title="__('Images dans le contenu')"
                                :icon="ImagesIcon"
                                :tooltip="__('Générer des images dans le contenu')"
                                size="small" />
                            <FormCheckboxGroupOption
                                v-model="modelValue.settings.youtube.enabled"
                                :title="__('Vidéos YouTube')"
                                :icon="YouTubeIcon"
                                :tooltip="__('Active l\'ajout de vidéos YouTube')"
                                size="small" />
                        </div>
                    </FormCheckboxGroup>
                </div>

                <div v-if="modelValue.settings.images.content.enabled" class="max-w-xs">
                    <FormInput
                        v-model="modelValue.settings.images.content.max_images"
                        type="number"
                        :label="__('Nombre maximum d\'images dans le contenu')"
                        min="0" />
                </div>

                <FormTextarea
                    v-if="modelValue.settings.images.thumbnail.enabled || modelValue.settings.images.content.enabled"
                    v-model="modelValue.settings.custom_instructions.images"
                    :label="__('Instructions personnalisées pour les images')"
                    :placeholder="__('Exemple : Créer des images avec un style noir et blanc')"
                    :rows="5"
                    :left-actions="[
                        {
                            id: 'magic-prompt',
                        },
                    ]">
                </FormTextarea>
            </DisclosurePanel>
        </Disclosure>

        <Disclosure as="div" v-slot="{ open }" :defaultOpen="true">
            <DisclosureButton
                :class="[
                    open ? 'rounded-t-lg' : 'rounded-lg',
                    'w-full flex justify-between px-6 py-3 text-sm font-medium text-left text-gray-900 border border-gray-300 dark:text-white dark:border-gray-800 focus:outline-none focus-visible:ring-gray-500/75 focus-visible:ring hover:bg-gray-50 dark:hover:bg-gray-900',
                ]">
                <span>{{ __("Liens internes et externes") }}</span>
                <ChevronUpIcon :class="open ? 'rotate-180 transform' : ''" class="w-5 h-5 text-gray-900 dark:text-white" />
            </DisclosureButton>
            <DisclosurePanel as="div" class="px-6 py-6 space-y-6 rounded-b-lg border-r border-b border-l border-gray-300 dark:border-gray-800">
                <FormCheckboxGroup>
                    <div class="grid grid-cols-2 gap-3 mt-2 2xl:grid-cols-3 lg:grid-cols-2 sm:grid-cols-3">
                        <FormCheckboxGroupOption
                            v-model="modelValue.settings.internal_links.enabled"
                            :title="__('Liens internes')"
                            :icon="LinkIcon"
                            :tooltip="__('Active l\'ajout de liens internes')"
                            size="small" />
                        <FormCheckboxGroupOption
                            v-model="modelValue.settings.external_links.enabled"
                            :title="__('Liens externes')"
                            :icon="ExternalLinkIcon"
                            :tooltip="__('Active l\'ajout de liens externes')"
                            size="small" />
                        <FormCheckboxGroupOption
                            v-model="modelValue.settings.custom_links.enabled"
                            :title="__('Liens personnalisés')"
                            :icon="Settings2Icon"
                            :tooltip="__('Active l\'ajout de liens personnalisés')"
                            size="small" />
                    </div>
                </FormCheckboxGroup>

                <CustomLinksPart v-if="modelValue.settings.custom_links.enabled" v-model="modelValue.settings.custom_links.links" />
            </DisclosurePanel>
        </Disclosure>
    </div>
</template>
